import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import {
  useCallback,
  useEffect,
  useMemo,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { analytics } from '../analytics';
import { captureWarning } from '../monitoring';

import { TourLayer } from './components/tour-layer';
import { filterSteps, isTourAvailable } from './engine/filter-steps';
import {
  initialTourState,
  tourReducer,
  withFinish,
  type EndReason,
  type TourMachineState,
} from './engine/machine';
import { isMobileViewport, useIsMobile, useReducedMotion } from './engine/media';
import { scrollTargetIntoView, waitForStableRect } from './engine/scroll';
import { tourCardStatus } from './engine/status';
import { DEFAULT_WAIT_MS, waitForTarget } from './engine/wait-for-target';
import { useTourProgress } from './progress/use-tour-progress';
import type { TourRegistry } from './registry';
import { tourResolveQueryOptions } from './resolve-query';
import { setTourSidebar } from './sidebar-bridge';
import { TourContext, type StartOptions, type TourContextValue } from './tour-context';
import type { TourAccess, TourDefinition, TourProgressAdapter, TourSource } from './types';

const ACTIVE_KEY = 'nucleus.tour.active';
/** Navigation that follows a try-it click (a link the step asked for) does not pause the tour. */
const ACTION_NAV_GRACE_MS = 1500;

interface StoredTour {
  tourId: string;
  index: number;
  history: number[];
  source: TourSource | null;
  params: Record<string, string>;
  href: string;
}

function readStored(): StoredTour | null {
  try {
    const raw = window.sessionStorage.getItem(ACTIVE_KEY);
    return raw ? (JSON.parse(raw) as StoredTour) : null;
  } catch {
    return null;
  }
}

function writeStored(value: StoredTour | null): void {
  try {
    if (value) window.sessionStorage.setItem(ACTIVE_KEY, JSON.stringify(value));
    else window.sessionStorage.removeItem(ACTIVE_KEY);
  } catch {
    // Private mode or blocked storage: a reload simply forgets the tour.
  }
}

export interface TourProviderProps {
  registry: TourRegistry;
  access: TourAccess;
  progressAdapter: TourProgressAdapter;
  app: 'admin' | 'platform';
  /** False hides every entry point (runtime kill switch, two-factor gate, no session). */
  enabled: boolean;
  /** Support sessions read progress but never write it. */
  readOnly?: boolean;
  /** Store the store-scoped tours run in; the shell's current store. */
  storeId?: string | undefined;
  namespace?: string;
  values?: Record<string, string>;
  helpPath?: string;
  welcomeTourId?: string;
  children: ReactNode;
}

type RouterLike = ReturnType<typeof useRouter>;

function routePath(router: RouterLike, routeId: string): string | null {
  const route = (router.routesById as Record<string, { fullPath: string } | undefined>)[routeId];
  return route ? route.fullPath : null;
}

function isOnRoute(router: RouterLike, routeId: string, params: Record<string, string>): boolean {
  return router.state.matches.some(
    (match) =>
      match.routeId === routeId &&
      Object.entries(params).every(
        ([key, value]) => (match.params as Record<string, string | undefined>)[key] === value,
      ),
  );
}

function closeMenuAround(element: HTMLElement | null): void {
  const menu = element?.closest<HTMLElement>('[role="menu"]');
  if (!menu?.id || typeof PointerEvent !== 'function') return;
  const trigger = document.querySelector<HTMLElement>(`[aria-controls="${menu.id}"]`);
  trigger?.dispatchEvent(
    new PointerEvent('pointerdown', { bubbles: true, cancelable: true, button: 0 }),
  );
}

/**
 * Runs guided tours: one at a time, steps filtered by permission and viewport, navigation
 * through the app's router, progress saved through the app's adapter. Mount it inside the
 * shell (it reads the router and the session) and render pages inside it.
 */
export function TourProvider({
  registry,
  access,
  progressAdapter,
  app,
  enabled,
  readOnly = false,
  storeId,
  namespace = 'tours',
  values = {},
  helpPath = '/help',
  welcomeTourId = 'welcome',
  children,
}: TourProviderProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const reducedMotion = useReducedMotion();
  const progress = useTourProgress(progressAdapter, { enabled, readOnly });
  const [state, dispatch] = useReducer(tourReducer, initialTourState);
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const stateRef = useRef<TourMachineState>(state);
  const elementRef = useRef<HTMLElement | null>(null);
  const ownNavigation = useRef(0);
  const actionNavUntil = useRef(0);
  const returnFocus = useRef<HTMLElement | null>(null);
  const pausedHref = useRef<string | null>(null);
  const progressRef = useRef(progress);
  // Router callbacks and timers read the latest values without re-subscribing.
  useLayoutEffect(() => {
    stateRef.current = state;
    elementRef.current = element;
    progressRef.current = progress;
  });

  const filterContext = useMemo(() => ({ access, isMobile }), [access, isMobile]);

  const stepsFor = useCallback(
    (definition: TourDefinition) => filterSteps(definition.steps, filterContext),
    [filterContext],
  );
  const isAvailable = useCallback(
    (definition: TourDefinition) => enabled && isTourAvailable(definition, filterContext),
    [enabled, filterContext],
  );
  const statusOf = useCallback(
    (definition: TourDefinition) => tourCardStatus(definition, progress.records.get(definition.id)),
    [progress.records],
  );

  const navigate = useCallback(
    async (options: { to: string; params?: Record<string, string>; search?: unknown }) => {
      ownNavigation.current += 1;
      try {
        await router.navigate(options as Parameters<RouterLike['navigate']>[0]);
      } finally {
        ownNavigation.current -= 1;
      }
    },
    [router],
  );

  const resolveRecord = useCallback(
    async (definition: TourDefinition) => {
      const resolver = definition.resolve;
      if (!resolver) return {};
      return queryClient.query(tourResolveQueryOptions(resolver, storeId));
    },
    [queryClient, storeId],
  );

  const start = useCallback(
    async (tourId: string, { source }: StartOptions) => {
      const definition = registry.get(tourId);
      if (!definition || !isAvailable(definition)) return false;
      const steps = filterSteps(definition.steps, { access, isMobile: isMobileViewport() });
      if (definition.route.includes('$storeId') && !storeId) return false;
      let params: Record<string, string> = {};
      if (definition.scope === 'store' && storeId) params = { storeId };
      if (definition.scope === 'record') {
        const resolved = await resolveRecord(definition).catch(() => null);
        if (!resolved) return false;
        params = resolved;
      }
      const to = routePath(router, definition.route);
      if (!to) return false;

      if (stateRef.current.phase !== 'idle' && stateRef.current.phase !== 'done') {
        closeMenuAround(elementRef.current);
      }
      const active = document.activeElement;
      returnFocus.current = active instanceof HTMLElement ? active : null;
      setElement(null);
      dispatch({ type: 'start', tourId, steps: withFinish(steps), source, params, at: Date.now() });
      analytics.track('tour_started', {
        tourId,
        version: definition.version,
        app,
        source,
        steps: steps.length,
      });
      const onRoute = isOnRoute(router, definition.route, params);
      if (!onRoute) {
        await navigate({ to, params, search: definition.search ?? {} });
      } else if (definition.search) {
        const extra = definition.search;
        await navigate({
          to,
          params,
          search: (prev: Record<string, unknown>) => ({ ...prev, ...extra }),
        });
      }
      dispatch({ type: 'ready' });
      return true;
    },
    [access, app, isAvailable, navigate, registry, resolveRecord, router, storeId],
  );

  const next = useCallback(() => {
    closeMenuAround(elementRef.current);
    dispatch({ type: 'next' });
  }, []);
  const back = useCallback(() => {
    closeMenuAround(elementRef.current);
    dispatch({ type: 'back' });
  }, []);
  const end = useCallback((reason: EndReason) => dispatch({ type: 'end', reason }), []);

  const resume = useCallback(() => {
    const current = stateRef.current;
    if (current.phase !== 'paused') return;
    const href = pausedHref.current;
    pausedHref.current = null;
    void (async () => {
      if (href) {
        ownNavigation.current += 1;
        try {
          await router.navigate({ href } as Parameters<RouterLike['navigate']>[0]);
        } finally {
          ownNavigation.current -= 1;
        }
      }
      dispatch({ type: 'resume' });
    })();
  }, [router]);

  const dismissWithoutRunning = useCallback(
    (tourId: string) => {
      const definition = registry.get(tourId);
      if (definition) progressRef.current.save(tourId, definition.version, 'dismissed');
    },
    [registry],
  );

  // Restore a tour that was running in this tab before a reload, once.
  const restored = useRef(false);
  useEffect(() => {
    if (!enabled || restored.current) return;
    restored.current = true;
    const stored = readStored();
    if (!stored) return;
    const definition = registry.get(stored.tourId);
    if (!definition) {
      writeStored(null);
      return;
    }
    const steps = withFinish(
      filterSteps(definition.steps, { access, isMobile: isMobileViewport() }),
    );
    pausedHref.current = stored.href;
    dispatch({
      type: 'restore',
      tourId: stored.tourId,
      steps,
      index: stored.index,
      history: stored.history,
      source: stored.source,
      params: stored.params,
      paused: !isOnRoute(router, definition.route, stored.params),
      at: Date.now(),
    });
  }, [access, enabled, registry, router]);

  useEffect(() => {
    if (!state.tourId || state.phase === 'idle' || state.phase === 'done') return;
    if (state.phase === 'starting') return;
    writeStored({
      tourId: state.tourId,
      index: state.index,
      history: [...state.history],
      source: state.source,
      params: state.params,
      href: pausedHref.current ?? router.state.location.href,
    });
  }, [router, state]);

  // A navigation the tour did not cause pauses it.
  useEffect(
    () =>
      router.subscribe('onBeforeNavigate', (event) => {
        const phase = stateRef.current.phase;
        if (phase !== 'waiting' && phase !== 'showing') return;
        if (ownNavigation.current > 0 || Date.now() < actionNavUntil.current) return;
        if (!event.pathChanged) return;
        pausedHref.current = event.fromLocation?.href ?? null;
        closeMenuAround(elementRef.current);
        dispatch({ type: 'pause' });
      }),
    [router],
  );

  // A step that points into the sidebar keeps it open while it waits and while it shows; it is
  // put back when the step changes or the tour stops. Declared before the step driver so the
  // sidebar is already opening when the target wait starts.
  const stepActive = state.phase === 'waiting' || state.phase === 'showing';
  const opensSidebar = stepActive && Boolean(state.steps[state.index]?.before?.openSidebar);
  useEffect(() => {
    if (!opensSidebar) return;
    setTourSidebar(true);
    return () => setTourSidebar(false);
  }, [opensSidebar, state.index, state.tourId]);

  // Drives the current step: before-hooks, waiting for the target, scrolling.
  useEffect(() => {
    if (state.phase !== 'waiting' || !state.tourId) return;
    const step = state.steps[state.index];
    if (!step) return;
    const tourId = state.tourId;
    const controller = new AbortController();
    // A function, so TypeScript does not narrow the flag across the awaits below.
    const stale = () => controller.signal.aborted;
    void (async () => {
      if (step.before?.search) {
        const extra = step.before.search;
        await navigate({
          to: router.state.location.pathname,
          search: (prev: Record<string, unknown>) => ({ ...prev, ...extra }),
        });
      }
      const targets = step.variants
        ? step.variants.map((variant) => variant.target)
        : step.target
          ? [step.target]
          : [];
      if (targets.length === 0) {
        if (!stale()) dispatch({ type: 'found', target: null });
        return;
      }
      const found = await waitForTarget(targets, {
        timeoutMs: step.waitMs ?? DEFAULT_WAIT_MS,
        signal: controller.signal,
        isReady: () => router.state.status === 'idle',
      });
      if (stale()) return;
      if (!found) {
        analytics.track('tour_target_missing', { tourId, stepId: step.id, target: targets[0] });
        if (!step.optional) {
          captureWarning('tour_target_missing', { tourId, stepId: step.id, targets });
        }
        dispatch({ type: 'timeout' });
        return;
      }
      // A target in a sheet that is still sliding in is measured where it lands.
      if (!reducedMotion) await waitForStableRect(found.element);
      if (stale()) return;
      await scrollTargetIntoView(found.element, { reducedMotion, isMobile: isMobileViewport() });
      if (stale()) return;
      setElement(found.element);
      const copy = step.variants?.find((variant) => variant.target === found.target)?.copy;
      dispatch({ type: 'found', target: found.target, ...(copy ? { copy } : {}) });
      analytics.track('tour_step_viewed', {
        tourId,
        stepId: step.id,
        index: state.index,
        kind: step.kind,
        variant: found.target,
      });
    })();
    return () => controller.abort();
  }, [navigate, reducedMotion, router, state.index, state.phase, state.steps, state.tourId]);

  // The step's element can re-render away (a row refetched); wait for it briefly, then treat
  // it as missing.
  useEffect(() => {
    if (state.phase !== 'showing' || !element) return;
    const id = window.setInterval(() => {
      if (element.isConnected) return;
      window.clearInterval(id);
      const step = stateRef.current.steps[stateRef.current.index];
      const target = stateRef.current.target;
      if (!step || !target) return;
      void waitForTarget([target], { timeoutMs: 1000 }).then((found) => {
        if (found) setElement(found.element);
        else dispatch({ type: 'timeout' });
      });
    }, 250);
    return () => window.clearInterval(id);
  }, [element, state.phase]);

  // Try-it steps advance when the person does the thing.
  useEffect(() => {
    if (state.phase !== 'showing' || !state.tourId) return;
    const step = state.steps[state.index];
    if (step?.kind !== 'action' || state.missing) return;
    const tourId = state.tourId;
    const advanceOn = step.advanceOn ?? { click: true };
    let fired = false;
    const fire = () => {
      if (fired) return;
      fired = true;
      actionNavUntil.current = Date.now() + ACTION_NAV_GRACE_MS;
      analytics.track('tour_action_done', { tourId, stepId: step.id });
      window.setTimeout(() => dispatch({ type: 'next' }), 0);
    };
    if ('click' in advanceOn && element) {
      const onKey = (event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') fire();
      };
      // Radix triggers open on pointerdown, after which the click may never reach the target.
      element.addEventListener('pointerdown', fire, true);
      element.addEventListener('click', fire, true);
      element.addEventListener('keydown', onKey, true);
      return () => {
        element.removeEventListener('pointerdown', fire, true);
        element.removeEventListener('click', fire, true);
        element.removeEventListener('keydown', onKey, true);
      };
    }
    if ('input' in advanceOn && element) {
      element.addEventListener('input', fire, true);
      return () => element.removeEventListener('input', fire, true);
    }
    if ('route' in advanceOn) {
      const routeId = advanceOn.route;
      return router.subscribe('onResolved', () => {
        if (router.state.matches.some((match) => match.routeId === routeId)) fire();
      });
    }
    if ('appears' in advanceOn) {
      const controller = new AbortController();
      void waitForTarget([advanceOn.appears], {
        timeoutMs: 10 * 60_000,
        signal: controller.signal,
      }).then((found) => {
        if (found) fire();
      });
      return () => controller.abort();
    }
    return undefined;
  }, [element, router, state.index, state.missing, state.phase, state.steps, state.tourId]);

  // Finishing or ending: save, report, give focus back.
  useEffect(() => {
    if (state.phase !== 'done' || !state.tourId) return;
    const definition = registry.get(state.tourId);
    const step = state.steps[state.index];
    if (definition && state.outcome) {
      progressRef.current.save(definition.id, definition.version, state.outcome);
      if (state.outcome === 'completed') {
        analytics.track('tour_completed', {
          tourId: definition.id,
          version: definition.version,
          durationMs: Date.now() - state.startedAt,
        });
      } else {
        analytics.track('tour_dismissed', {
          tourId: definition.id,
          version: definition.version,
          stepId: step?.id,
          index: state.index,
          reason: state.endReason,
        });
      }
    }
    closeMenuAround(elementRef.current);
    writeStored(null);
    pausedHref.current = null;
    const target = returnFocus.current;
    returnFocus.current = null;
    window.setTimeout(() => {
      if (target?.isConnected) target.focus();
      else document.querySelector<HTMLElement>('main h1, h1')?.focus();
    }, 0);
    dispatch({ type: 'reset' });
  }, [registry, state]);

  const value = useMemo<TourContextValue>(
    () => ({
      registry,
      access,
      namespace,
      values,
      enabled,
      helpPath,
      welcomeTourId,
      storeId,
      state,
      element,
      isMobile,
      reducedMotion,
      progress,
      isAvailable,
      statusOf,
      stepsFor,
      start,
      next,
      back,
      end,
      resume,
      dismissWithoutRunning,
      announce: setAnnouncement,
      announcement,
      resolveRecord,
    }),
    [
      access,
      announcement,
      back,
      dismissWithoutRunning,
      element,
      enabled,
      end,
      helpPath,
      isAvailable,
      isMobile,
      namespace,
      next,
      progress,
      reducedMotion,
      registry,
      resolveRecord,
      resume,
      start,
      state,
      statusOf,
      stepsFor,
      storeId,
      values,
      welcomeTourId,
    ],
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      {enabled ? <TourLayer /> : null}
    </TourContext.Provider>
  );
}
